import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { EnvironmentConfig } from '@/configs/environment.config';
import { ModuloResponse } from '@/models/modulo.model';
import { UsuarioResponse } from '@/models/usuario.model';
import { UsuarioModulosRequest } from '@/models/usuario-modulos.model';

@Injectable({
    providedIn: 'root'
})
export class PermissoesService {
    private readonly http = inject(HttpClient);
    private readonly apiUrl = inject(EnvironmentConfig).getApiUrl() + '/usuarios';

    getModulosUsuario(usuarioId: string): Observable<ModuloResponse[]> {
        return this.http.get<ModuloResponse[]>(`${this.apiUrl}/${usuarioId}/modulos`);
    }

    vincularModulos(usuarioId: string, modulosIds: number[]): Observable<UsuarioResponse> {
        const request: UsuarioModulosRequest = {
            usuarioId: usuarioId,
            modulosIds: modulosIds
        };

        return this.http.put<UsuarioResponse>(`${this.apiUrl}/modulos`, request);
    }

    adicionarModulo(usuarioId: string, moduloId: number): Observable<UsuarioResponse> {
        return this.http.post<UsuarioResponse>(`${this.apiUrl}/${usuarioId}/modulos/${moduloId}`, {});
    }

    removerModulo(usuarioId: string, moduloId: number): Observable<UsuarioResponse> {
        return this.http.delete<UsuarioResponse>(`${this.apiUrl}/${usuarioId}/modulos/${moduloId}`);
    }

    removerTodosModulos(usuarioId: string): Observable<UsuarioResponse> {
        return this.http.delete<UsuarioResponse>(`${this.apiUrl}/${usuarioId}/modulos`);
    }

    hasPermission(usuario: UsuarioResponse | null, moduloNome: string): boolean {
        if (!usuario) {
            return false;
        }

        if (usuario.superintendente) {
            return true;
        }

        if (!usuario.modulos || usuario.modulos.length === 0) {
            return false;
        }

        const temPermissaoDireta = usuario.modulos.some((m) => m.nome === moduloNome && m.ativo);

        if (temPermissaoDireta) {
            return true;
        }

        return this.hasPermissaoAdmin(usuario, moduloNome);
    }

    private hasPermissaoAdmin(usuario: UsuarioResponse, moduloNome: string): boolean {
        if (!usuario.modulos || usuario.modulos.length === 0) {
            return false;
        }

        if (moduloNome.startsWith('MotoClube')) {
            return usuario.modulos.some((m) => m.nome === 'MotoClubeAdministrador' && m.ativo);
        }

        return false;
    }

    hasAnyPermission(usuario: UsuarioResponse | null, modulosNomes: string[]): boolean {
        if (!usuario) {
            return false;
        }

        return modulosNomes.some((nome) => this.hasPermission(usuario, nome));
    }

    hasAllPermissions(usuario: UsuarioResponse | null, modulosNomes: string[]): boolean {
        if (!usuario) {
            return false;
        }

        return modulosNomes.every((nome) => this.hasPermission(usuario, nome));
    }

    isSuperintendente(usuario: UsuarioResponse | null): boolean {
        return usuario?.superintendente === true;
    }

    isAdministradorMotoClube(usuario: UsuarioResponse | null): boolean {
        if (!usuario || !usuario.modulos) {
            return false;
        }

        return usuario.modulos.some((m) => m.nome === 'MotoClubeAdministrador' && m.ativo);
    }
}
